import { bool, func, number, object, string } from "prop-types";
import React, { useState } from "react";

import styled from "styled-components/macro";

import { useNetworkMetadata } from "Flocknote/apiHooks";

import {
  useGetAvailableBoards,
  useLinkColumnToBoard,
} from "Boards/APIReactQuery/queries";
import { getGroupsWithNestedLevel } from "Boards/Utils/nestedGroupUtils";

import { FnrPopover } from "UI/FnrPopover2";

import GroupAndReportSelectors from "./GroupAndReportSelectors";
import LoadingBricks from "./LoadingBricks";
import SelectionComplete from "./SelectionComplete";
import { getReports, getSelectedBoard, getSelectedBoardName } from "./utils";

ShareWithAnotherBoardPopover.propTypes = {
  buttonRef: object,
  show: bool,
  onShouldClose: func,
  columnID: number,
  columnTitle: string,
  currentBoardID: number,
};

export default function ShareWithAnotherBoardPopover({
  buttonRef,
  show,
  onShouldClose,
  columnID,
  columnTitle,
  currentBoardID,
}) {
  const [networkMetaData] = useNetworkMetadata();
  const networkLogo = networkMetaData?.networkLogo;

  const { data, isLoading } = useGetAvailableBoards({
    columnID,
    currentBoardID,
    enabled: false,
  });

  const [activeElement, setActiveElement] = useState("selecting"); // "selecting" || "complete"

  // toggling dropdowns
  const [showGroups, setShowGroups] = useState(false);
  const [showReports, setShowReports] = useState(false);

  const toggleGroups = () => {
    if (!showGroups) setShowReports(false);
    setShowGroups((prevShow) => !prevShow);
  };

  const toggleReports = () => {
    setShowReports((prevShow) => !prevShow);
  };

  // selecting dropdown options
  const [selectedGroupID, setSelectedGroupID] = useState("allreports");
  const [selectedBoardID, setSelectedBoardID] = useState();

  const handleGroupSelection = (groupID) => {
    if (groupID === selectedGroupID) return;

    setSelectedBoardID(undefined);
    setSelectedGroupID(groupID);
  };

  const handleBoardSelection = (boardID) => {
    if (boardID === selectedBoardID) return;

    setSelectedBoardID(boardID);
  };

  const groupsWithDisabled =
    data?.groups?.map((g) => {
      let group = {
        ...g,
        name: g?.isEveryoneGroup ? `${g.name} (Everyone)` : g.name,
        disabled: !g?.boards?.length,
      };
      return group;
    }) || [];
  const groupsWithLevels = getGroupsWithNestedLevel({
    groups: groupsWithDisabled,
  });
  const groups = [
    {
      ID: "allreports",
      name: "All Reports",
      level: 0,
      image: networkLogo,
    },
    ...groupsWithLevels,
  ];

  const reports = getReports({ selectedGroupID, data });

  const selectedBoardName = getSelectedBoardName({ selectedBoardID, data });

  const titleText =
    activeElement === "selecting" ? "Add to Another Report" : "Added!";

  // Saving column to board
  const groupIDForSaving =
    typeof selectedGroupID === "number" ? selectedGroupID : undefined;
  const { mutate: save, isLoading: isSaving } = useLinkColumnToBoard({
    columnID,
    boardID: selectedBoardID,
    groupID: groupIDForSaving,
    onSuccess: (resp) => {
      setActiveElement("complete");
    },
  });

  // Actions when done
  const handleClose = () => {
    setActiveElement("selecting");
    setSelectedGroupID("allreports");
    setSelectedBoardID(undefined);
    setShowGroups(false);
    setShowReports(false);
    onShouldClose();
  };

  const goToReport = () => {
    const isReportBoard = selectedGroupID === "allreports";

    if (isReportBoard) {
      let url = `/reports/${selectedBoardID}`;
      window.location = url;
    } else {
      let board = getSelectedBoard({
        selectedBoardID,
        selectedGroupID,
        data,
      });

      let url = `/${board.groupSlug}/board/${board.boardSlug}`;
      window.location = url;
    }
  };

  return (
    <FnrPopover
      buttonRef={buttonRef}
      show={show}
      onShouldClose={handleClose}
      dataElementToIgnore="fnr-popover-option"
    >
      <Content>
        <Row paddingInline="6px">
          <TitleIcon className="fad fa-clipboard-check" />
          <Title>{titleText}</Title>
        </Row>

        {isLoading && <LoadingBricks />}

        {!isLoading && activeElement === "selecting" && (
          <GroupAndReportSelectors
            groups={groups}
            selectedGroupID={selectedGroupID}
            handleGroupSelection={handleGroupSelection}
            showGroups={showGroups}
            toggleGroups={toggleGroups}
            reports={reports}
            selectedBoardID={selectedBoardID}
            handleBoardSelection={handleBoardSelection}
            showReports={showReports}
            toggleReports={toggleReports}
            handleClose={handleClose}
            save={save}
            isSaving={isSaving}
          />
        )}

        {!isLoading && activeElement === "complete" && (
          <SelectionComplete
            handleClose={handleClose}
            columnTitle={columnTitle}
            selectedBoardName={selectedBoardName}
            goToReport={goToReport}
          />
        )}
      </Content>
    </FnrPopover>
  );
}

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 8px;
  box-sizing: border-box;

  .HoverBackground {
    background: rgba(255, 255, 255, 0.4);
  }
`;

const Row = styled.div`
  display: flex;
  justify-content: ${(p) => p.justifyContent || "flex-start"};
  align-items: center;
  gap: 6px;
  padding-inline: ${(p) => p.paddingInline || 0};
`;
const Title = styled.div`
  font-size: 24px;
`;
const TitleIcon = styled.div`
  font-size: 24px;
`;
